import {
	PRESENTATION_RICH_TEXT_MARK_SET,
	PRESENTATION_RICH_TEXT_NODE_SET,
	type PresentationRichTextBlock,
	type PresentationRichTextInline,
	type PresentationRichTextMark,
	type PresentationRichTextNode,
	type PresentationRichTextV1,
	presentationRichTextV1Schema,
} from "./presentation-rich-text-v1";

function asRecord(value: unknown): Record<string, unknown> {
	return value != null && typeof value === "object" && !Array.isArray(value)
		? (value as Record<string, unknown>)
		: {};
}

function filterMarks(marks: unknown): PresentationRichTextMark[] | undefined {
	if (!Array.isArray(marks) || marks.length === 0) {
		return undefined;
	}
	const out: PresentationRichTextMark[] = [];
	for (const mark of marks) {
		const type =
			typeof mark === "string"
				? mark
				: typeof asRecord(mark).type === "string"
					? String(asRecord(mark).type)
					: "";
		if (PRESENTATION_RICH_TEXT_MARK_SET.has(type)) {
			out.push(type as PresentationRichTextMark);
		}
	}
	return out.length > 0 ? out : undefined;
}

function parseInline(node: unknown): PresentationRichTextInline | null {
	const row = asRecord(node);
	const type = typeof row.type === "string" ? row.type : "";
	if (type === "hardBreak" || type === "hard_break") {
		return { type: "hardBreak" };
	}
	// TipTap refuses empty text nodes, so none are kept.
	if (
		type === "text" &&
		typeof row.text === "string" &&
		row.text.length > 0
	) {
		const marks = filterMarks(row.marks);
		return { type: "text", text: row.text, ...(marks ? { marks } : {}) };
	}
	if (type === "dynamicAttributeChip") {
		const attrs = asRecord(row.attrs);
		const token = typeof attrs.token === "string" ? attrs.token : "";
		if (token) {
			return { type: "text", text: token };
		}
	}
	return null;
}

function parseInlines(nodes: unknown): PresentationRichTextInline[] {
	if (!Array.isArray(nodes)) {
		return [];
	}
	const out: PresentationRichTextInline[] = [];
	for (const node of nodes) {
		const inline = parseInline(node);
		if (inline) {
			out.push(inline);
		}
	}
	return out;
}

function parseBlock(node: unknown): PresentationRichTextNode | null {
	const row = asRecord(node);
	const type = typeof row.type === "string" ? row.type : "";

	if (!PRESENTATION_RICH_TEXT_NODE_SET.has(type) && type !== "doc") {
		// Unknown block node: try to salvage text descendants as a paragraph.
		const salvaged = salvagePlainParagraph(node);
		return salvaged;
	}

	if (type === "paragraph") {
		return {
			type: "paragraph",
			content: parseInlines(row.content),
		};
	}

	if (type === "bulletList" || type === "orderedList") {
		const items: Array<{
			type: "listItem";
			content?: PresentationRichTextBlock[];
		}> = [];
		if (Array.isArray(row.content)) {
			for (const child of row.content) {
				const item = parseBlock(child);
				if (item?.type === "listItem") {
					items.push(item);
				} else if (item) {
					items.push({ type: "listItem", content: [item] });
				}
			}
		}
		return type === "bulletList"
			? { type: "bulletList", content: items }
			: { type: "orderedList", content: items };
	}

	if (type === "listItem") {
		const children: PresentationRichTextBlock[] = [];
		if (Array.isArray(row.content)) {
			for (const child of row.content) {
				const block = parseBlock(child);
				if (block && block.type !== "listItem") {
					children.push(block);
				}
			}
		}
		return { type: "listItem", content: children };
	}

	if (type === "text" || type === "hardBreak") {
		const inline = parseInline(node);
		return inline ? { type: "paragraph", content: [inline] } : null;
	}

	return salvagePlainParagraph(node);
}

function salvagePlainParagraph(
	node: unknown,
): PresentationRichTextBlock | null {
	const text = collectDescendantText(node).trim();
	if (text.length === 0) {
		return null;
	}
	return {
		type: "paragraph",
		content: [{ type: "text", text }],
	};
}

function collectDescendantText(node: unknown): string {
	const row = asRecord(node);
	if (typeof row.text === "string") {
		return row.text;
	}
	if (!Array.isArray(row.content)) {
		return "";
	}
	return row.content.map(collectDescendantText).join("");
}

/**
 * Convert TipTap (or TipTap-like) JSON into PresentationRichTextV1.
 * Unknown marks/nodes are stripped or salvaged as plain text — never throws.
 */
export function tiptapDocToPresentationRichText(
	doc: unknown,
): PresentationRichTextV1 {
	const row = asRecord(doc);
	const blocks: PresentationRichTextBlock[] = [];

	const rootContent =
		row.type === "doc" && Array.isArray(row.content)
			? row.content
			: Array.isArray(doc)
				? doc
				: Array.isArray(row.content)
					? row.content
					: [];

	for (const node of rootContent) {
		const block = parseBlock(node);
		if (block && block.type !== "listItem") {
			blocks.push(block);
		}
	}

	const result: PresentationRichTextV1 = {
		type: "doc",
		version: 1,
		content: blocks,
	};

	const parsed = presentationRichTextV1Schema.safeParse(result);
	if (parsed.success) {
		return parsed.data;
	}

	return { type: "doc", version: 1, content: [] };
}

/** Build rich text from a plain string (no marks). */
export function plainTextToPresentationRichText(
	text: string,
): PresentationRichTextV1 {
	const paragraphs = text.length === 0 ? [""] : text.split(/\n\n+/);
	return {
		type: "doc",
		version: 1,
		content: paragraphs.map((paragraph) => {
			const lines = paragraph.split("\n");
			const content: PresentationRichTextInline[] = [];
			for (let i = 0; i < lines.length; i++) {
				if (i > 0) {
					content.push({ type: "hardBreak" });
				}
				const line = lines[i] ?? "";
				if (line.length > 0) {
					content.push({ type: "text", text: line });
				}
			}
			return { type: "paragraph", content };
		}),
	};
}

/**
 * Read `content.richText` from a block content bag when valid; else null.
 */
export function readBlockPresentationRichText(
	content: unknown,
): PresentationRichTextV1 | null {
	const row = asRecord(content);
	if (row.richText == null) {
		return null;
	}
	const parsed = presentationRichTextV1Schema.safeParse(row.richText);
	return parsed.success ? parsed.data : null;
}
